import cloneDeep from 'lodash/cloneDeep';

import {
  isEventBasedType,
  isEventEntry,
  isGroupedEntry,
  isUserBasedType,
  QueryEntry,
  QueryEventEntry,
  QuerySegmentEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {MATH_TYPE, PRIMITIVE_MATH_TYPES, ResourceType, XAU_MATH_TYPES} from 'common/report/constants';
import {
  isCohortSegment,
  isEventSegment,
  isUserSegment,
  OtherSegmentation,
  Segment,
  SegmentValue,
} from 'common/query-builder/query-block/segment-menu/types';
import {ButtonTheme} from 'common/components/button/constants';
import {SelectTheme} from 'common/components/select/constants';
import {getReportName} from 'common/report/util';
import {Segmentation} from 'common/types/reports/query';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {isBehaviorFilter, isCohortFilter, isUserFilter} from 'common/widgets/property-filter-menu/models/utils';
import {
  isBehaviorSegmentation,
  isMultiAttributionSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {FeatureRolloutName, FeatureRollouts} from 'common/report/util/feature-sampling';
import {CurrentProject} from 'common/report/model-resources/projects';
import {MultiAttributionType} from 'common/widgets/multi-attribution-menu/types';

export const queryBuilderUtils = {
  isCoreReport,
};

/**
 * Sanitizes exclusionSteps by fixing out of range step selections
 */
export function sanitizeExclusionSteps(exclusionSteps: QueryEventEntry[], events: QueryEntry[]): QueryEventEntry[] {
  return exclusionSteps.map((step) => {
    if (`to` in step.excludeRange && step.excludeRange.to > events.length) {
      step.excludeAll = true;
      step.excludeRange = {};
    }

    return step;
  });
}

// The format used by mp-query-builder and mp-query-entry-section are different. So it's necessary to map
// between these two formats.
export function transformToQuerySegmentEntries(segments: Segment[]): QuerySegmentEntry[] {
  return cloneDeep(segments).map((segment) => {
    let customBucket;
    let segmentValue: SegmentValue;

    if (isCohortSegment(segment)) {
      segmentValue = segment;
    } else if (Array.isArray(segment)) {
      // Value comparisons shouldnt have custom bucketing so dont need to remove
      segmentValue = segment.map((seg) => Object.values(seg)[0]);
    } else {
      const segmentation: Segmentation = Object.values(segment)[0];
      customBucket = segmentation.customBucket;
      delete segmentation.customBucket;
      // The QuerySegmentEntry format has the `customBucket` as a top-level field. So we remove `customBucket`
      // from the segment and move it to be a top-level field.
      segmentValue = segmentation;
    }

    return {
      event: segmentValue,
      filters: [],
      type: SelectorTypeDefinitions.Segment,
      ...(customBucket ? {customBucket} : {}),
    };
  });
}

/**
 * Insert the custom bucketing entry field into the segment fields
 * @todo [pablo] refactor this so that query-entry-section looks at the
 * segment value to see if custom bucketing is enabled or not
 */
export function transformFromQuerySegmentEntries(entries: QuerySegmentEntry[]): Segment[] {
  return entries.map((entry) => {
    if (isCohortSegment(entry.event)) {
      return entry.event;
    }

    const segmentationArray = [].concat(entry.event).map((event) => ({
      ...event,
      ...(entry.customBucket ? {customBucket: entry.customBucket} : {}),
    }));
    const segmentations = segmentationArray.map((segment) => {
      switch (segment.resourceType) {
        case ResourceType.Event:
          return {[ResourceType.Event]: segment};
        case ResourceType.User:
          return {[ResourceType.User]: segment};
        case ResourceType.Other:
          return {[ResourceType.Other]: segment as OtherSegmentation};
        case ResourceType.Cohort:
        default:
          throw new Error(`unknown segment type: ${segment.resourceType}`);
      }
    });
    return segmentations.length === 1 ? segmentations[0] : segmentations;
  });
}

function hasSupportedAttributionCountType(entry: QueryEntry): boolean {
  return (
    isEventEntry(entry) &&
    [PRIMITIVE_MATH_TYPES.TOTAL, PRIMITIVE_MATH_TYPES.UNIQUE].includes(
      entry.aggregationOperator as PRIMITIVE_MATH_TYPES,
    )
  );
}

function hasSupportedAttributionType(type: MultiAttributionType): boolean {
  // These attribution types are compatible with all count types
  return [MultiAttributionType.LastTouch, MultiAttributionType.FirstTouch, MultiAttributionType.Participation].includes(
    type,
  );
}

export function isSupportedMultiAttributionTypeForEntry(entry: QueryEntry, type: MultiAttributionType): boolean {
  // There are a set of attribution types that are compatible with all count
  // types and a set of count types that are compatible with all attribution
  // types. If they have one of either and they're good, if they don't then the
  // pairing is incompatible.
  return hasSupportedAttributionCountType(entry) || hasSupportedAttributionType(type);
}

function isMultiAttributionSegment(segment: Segment) {
  return isEventSegment(segment) && isMultiAttributionSegmentation(segment.event);
}

function isBehaviorSegment(segment: Segment) {
  return isUserSegment(segment) && isBehaviorSegmentation(segment.user);
}

/**
 * Sanitizes group-by entries when a cohort entry is selected
 */
export function sanitizeGroupBys(events: QueryEntry[], segments: Segment[]) {
  const eventsOnlyEntries = events.every((entry) => isEventBasedType(entry.type) || isGroupedEntry(entry));
  const peopleOnlyEntries = events.length > 0 && events.every((entry) => isUserBasedType(entry));
  const hasXau = events.some((entry) => isXauMathType(entry.aggregationOperator));
  let results = segments;

  if (peopleOnlyEntries || !eventsOnlyEntries) {
    results = results.filter(
      (segment) => isCohortSegment(segment) || (isUserSegment(segment) && !isBehaviorSegmentation(segment.user)),
    );
  }
  if (hasXau) {
    results = results.filter((segment) => !(isBehaviorSegment(segment) || isMultiAttributionSegment(segment)));
  }

  const everyMultiAttributionEventIsSupported = events.every((entry) => {
    // We can ignore the case where entry.multiAttribution is blank. This is the
    // case when we are adding the multi attribution breakdown for the first
    // time, in which case we'll only add a compatible multi attribution type.
    if (isEventEntry(entry) && entry.multiAttribution) {
      return isSupportedMultiAttributionTypeForEntry(entry, entry.multiAttribution.type);
    }
    return true;
  });

  // For multi attribution we want to remove the entire breakdown if any event
  // is not supported in the query.
  results = !everyMultiAttributionEventIsSupported
    ? results.filter((segment) => !isMultiAttributionSegment(segment))
    : results;

  return results;
}

/**
 * Sanitizes filter entries when a cohort entry is selected
 */
export function sanitizeFilters(events: QueryEntry[], filters: PropertyFilter[]) {
  const eventsOnlyEntries = events.every((entry) => isEventBasedType(entry.type) || isGroupedEntry(entry));
  const peopleOnlyEntries = events.length > 0 && events.every((entry) => isUserBasedType(entry));
  const hasXau = events.some((entry) => isXauMathType(entry.aggregationOperator));
  let results = filters;
  if (hasXau) {
    results = filters.filter((segment) => !isBehaviorFilter(segment));
  }

  if (peopleOnlyEntries || !eventsOnlyEntries) {
    // TODO remove behavior stuff once it is no longer user resource type
    // only keep cohort filter and user property filters
    results = results.filter(
      (segment) => isCohortFilter(segment) || (isUserFilter(segment) && !isBehaviorSegmentation(segment)),
    );
  }
  return results;
}

function isCoreReport() {
  return [`dashboards`, `funnels`, `insights`, `retention`, `flows`].includes(getReportName());
}

export function hasInlineBehaviors(project: CurrentProject) {
  return FeatureRollouts[FeatureRolloutName.QBInlineBehaviors].isEnabled(project);
}

/** TODO(pablo): drop this method once internal testing is completed */
export const qbStyleAttrs = (forNewUI = null) => ({
  selectorTheme: forNewUI ?? isCoreReport() ? SelectTheme.QueryBuilder : SelectTheme.Select,
  selectorType: `button`,
  buttonTheme: forNewUI ?? isCoreReport() ? ButtonTheme.Tertiary : ButtonTheme.Select,
});

export function isXauMathType(value: MATH_TYPE): value is XAU_MATH_TYPES {
  return Object.values(XAU_MATH_TYPES).includes(value as XAU_MATH_TYPES);
}
